import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import encodeFormData from "@splitfire-agency/raiden-library/dist/libraries/utils/encodeFormData";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import InputFile from "../../../components/Form/InputFile";
import {
  useApiError,
  withApiError,
} from "../../../components/ReactHookForm/ApiError";
import ApiErrorDetailRHF from "../../../components/ReactHookForm/ApiErrorDetailRHF";
import FormController from "../../../components/ReactHookForm/FormController";
import FormStateRHF from "../../../components/ReactHookForm/FormStateRHF";
import { AUTHORIZATIONS_AUTHORIZATION_VALUE_CUSTOMERS_DOCUMENTS_UPDATE } from "../../../constants/authorizations";
import { CUSTOMERS_DOCUMENTS_STATES_VALUE_PENDING } from "../../../constants/customersDocuments";
import useAuthAs from "../../../hooks/auth/as";
import useCanPerform from "../../../hooks/useCanPerform";
import useRequest from "../../../hooks/useRequest";
import generateApiUri from "../../../libraries/utils/generateApiUri";

const CustomersDocumentsPagesActionCreate = withApiError(
  /**
   * @typedef {Object} Props
   * @property {number} documentId
   * @property {import("../../../types/CustomerDocument").CustomerDocument} [customerDocument]
   * @property {() => void} mutateCustomerDocument
   * @property {(props: { isAllowed: boolean, trigger: () => void }) => import("react").ReactElement | null} render
   *
   * @typedef {Props} Merged
   *
   * @param {Merged} props
   */
  function CustomersDocumentsPagesActionCreate({
    documentId,
    mutateCustomerDocument,
    customerDocument,
    render,
  }) {
    const { isOpen, onOpen, onClose: _onClose } = useDisclosure();

    const canPerform = useCanPerform();

    const intl = useIntl();

    const { user } = useAuthAs();

    const form = useForm({
      defaultValues: {
        pages: [],
      },
    });
    const { handleSubmit, reset } = form;

    const { request, isLoading, toastSuccess } = useRequest();

    const { resolveApiError, resetApiErrors } = useApiError();

    const onClose = useCallback(() => {
      _onClose();
      form.reset();
    }, [_onClose, form]);

    function onSubmit(fields) {
      request(
        generateApiUri({
          id: "@customers.documents.pages.create",
          parameters: { documentId },
        }),
        {
          method: "POST",
          body: encodeFormData({
            ...fields,
          }),
        },
        {
          inputMode: "formData",
        },
      )
        .then(() => {
          toastSuccess({ title: "Document téléversé avec succès" });
          mutateCustomerDocument();
          onClose();
          reset();
          resetApiErrors();
        })
        .catch((response) => {
          resolveApiError({ response, form });
        });
    }

    const formId = `customers-documents-pages-action-upload-${documentId}`;

    return (
      <>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />

          <ModalContent>
            <FormProvider {...form}>
              <ModalHeader>
                {intl.formatMessage({
                  defaultMessage: "Téléverser des documents",
                })}
              </ModalHeader>

              <ModalCloseButton />

              <ModalBody>
                <form id={formId} onSubmit={handleSubmit(onSubmit)}>
                  <ApiErrorDetailRHF pb="1rem" />

                  <FormController
                    name="pages"
                    rules={{ required: true }}
                    renderWithFormControl={(field) => (
                      <InputFile {...field} isMultiple={true} />
                    )}
                  />
                </form>
              </ModalBody>

              <ModalFooter>
                <Wrap spacing=".5rem">
                  <Button
                    onClick={onClose}
                    variant="outline"
                    colorScheme="gray">
                    {intl.formatMessage({ defaultMessage: "Fermer" })}
                  </Button>

                  <FormStateRHF
                    render={({ isDirty }) => (
                      <Button
                        type="submit"
                        form={formId}
                        isDisabled={!isDirty}
                        isLoading={isLoading}
                        colorScheme="brandSecondary">
                        {intl.formatMessage({ defaultMessage: "Téléverser" })}
                      </Button>
                    )}
                  />
                </Wrap>
              </ModalFooter>
            </FormProvider>
          </ModalContent>
        </Modal>

        {render({
          isAllowed:
            customerDocument?.state ===
              CUSTOMERS_DOCUMENTS_STATES_VALUE_PENDING &&
            (canPerform({
              authorizations:
                AUTHORIZATIONS_AUTHORIZATION_VALUE_CUSTOMERS_DOCUMENTS_UPDATE,
            }) ||
              user?.user_type === "customer"),
          trigger: onOpen,
        })}
      </>
    );
  },
);

export default CustomersDocumentsPagesActionCreate;
